import * as THREE from "three";
import type {
  ExperienceSettings,
  FrameContext,
  RuntimeSystem,
  TimePreset,
  WeatherKind,
  WorldQuery,
} from "../core/types";
import { seededRandom } from "../core/random";

type AmbientEventKind = "bird" | "insect" | "frog" | "drip" | "boat";

interface Voice {
  sources: AudioScheduledSourceNode[];
  gain: GainNode;
}

interface SoundscapeMix {
  wind: number;
  water: number;
  rain: number;
  insects: number;
  biophony: number;
  anthrophony: number;
}

interface NaturalAudioCue {
  kind: "stone-step" | "earth-step" | "wade" | "vegetation-brush";
  intensity: number;
  position: THREE.Vector3;
}

const WEATHER_RAIN: Record<WeatherKind, number> = {
  clear: 0,
  cloudy: 0,
  overcast: 0.03,
  mist: 0.015,
  drizzle: 0.3,
  "after-rain": 0.045,
};

const TIME_ACTIVITY: Record<TimePreset, { birds: number; insects: number; frogs: number; boats: number }> = {
  dawn: { birds: 1, insects: 0.1, frogs: 0.2, boats: 0.16 },
  morning: { birds: 0.82, insects: 0.04, frogs: 0.04, boats: 0.72 },
  noon: { birds: 0.24, insects: 0.18, frogs: 0, boats: 0.5 },
  "golden-hour": { birds: 0.54, insects: 0.48, frogs: 0.28, boats: 0.38 },
  night: { birds: 0.02, insects: 1, frogs: 1, boats: 0.04 },
};

export function resolveSoundscapeMix(
  settings: Pick<ExperienceSettings, "weather" | "timePreset" | "animals" | "residents">,
  nearbyWater: number,
): SoundscapeMix {
  const rain = WEATHER_RAIN[settings.weather];
  const activity = TIME_ACTIVITY[settings.timePreset];
  const weatherDucking = settings.weather === "drizzle" ? 0.4 : settings.weather === "overcast" ? 0.78 : 1;
  return {
    wind: settings.weather === "clear" ? 0.045 : settings.weather === "mist" ? 0.032 : 0.07,
    water: 0.018 + Math.max(0, Math.min(1, nearbyWater)) * 0.105,
    rain,
    insects: settings.animals ? activity.insects * weatherDucking * 0.024 : 0,
    biophony: settings.animals ? weatherDucking : 0,
    anthrophony: settings.residents ? weatherDucking : 0,
  };
}

export class AmbientEventScheduler {
  private readonly random: () => number;
  private nextEventAt = 0;

  constructor(random: () => number) {
    this.random = random;
  }

  next(elapsed: number, settings: Pick<ExperienceSettings, "weather" | "timePreset" | "animals" | "residents">): AmbientEventKind | null {
    if (elapsed < this.nextEventAt) return null;
    const activity = TIME_ACTIVITY[settings.timePreset];
    const choices: Array<readonly [AmbientEventKind, number]> = [
      ["bird", settings.animals ? activity.birds : 0],
      ["insect", settings.animals ? activity.insects * 0.5 : 0],
      ["frog", settings.animals ? activity.frogs * (settings.weather === "after-rain" ? 1.35 : 1) : 0],
      ["drip", settings.weather === "after-rain" || settings.weather === "drizzle" ? 0.7 : 0.08],
      ["boat", settings.residents ? activity.boats : 0],
    ];
    const total = choices.reduce((sum, [, weight]) => sum + weight, 0);
    this.nextEventAt = elapsed + 3.8 + this.random() * 8.5;
    if (total <= 0) return null;
    let selection = this.random() * total;
    for (const [kind, weight] of choices) {
      selection -= weight;
      if (selection <= 0 && weight > 0) return kind;
    }
    return null;
  }
}

export class AmbientAudioSystem implements RuntimeSystem {
  private readonly world: WorldQuery;
  private readonly random: () => number;
  private readonly scheduler: AmbientEventScheduler;
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private geophony: GainNode | null = null;
  private biophony: GainNode | null = null;
  private anthrophony: GainNode | null = null;
  private wind: Voice | null = null;
  private water: Voice | null = null;
  private rain: Voice | null = null;
  private insects: Voice | null = null;
  private transientSources = new Set<AudioScheduledSourceNode>();

  constructor(world: WorldQuery, seed = "jiangnan-audio") {
    this.world = world;
    this.random = seededRandom(`${seed}:soundscape`);
    this.scheduler = new AmbientEventScheduler(this.random);
  }

  async resume(): Promise<boolean> {
    try {
      if (!this.context) this.initialize();
      await this.context?.resume();
      return this.context?.state === "running";
    } catch {
      this.dispose();
      return false;
    }
  }

  update(frame: FrameContext): void {
    if (!this.context || !this.master) return;
    const now = this.context.currentTime;
    const volume = frame.paused ? frame.settings.masterVolume * 0.18 : frame.settings.masterVolume;
    this.master.gain.setTargetAtTime(volume, now, 0.12);

    const depth = this.world.getWaterDepth(frame.camera.position.x, frame.camera.position.z);
    const nearbyWater = depth > 0 ? 1 : this.sampleNearbyWater(frame.camera.position.x, frame.camera.position.z);
    const mix = resolveSoundscapeMix(frame.settings, nearbyWater);
    this.wind?.gain.gain.setTargetAtTime(mix.wind, now, 1.4);
    this.water?.gain.gain.setTargetAtTime(mix.water, now, 0.8);
    this.rain?.gain.gain.setTargetAtTime(mix.rain, now, 0.7);
    this.insects?.gain.gain.setTargetAtTime(mix.insects, now, 1.8);
    this.biophony?.gain.setTargetAtTime(mix.biophony, now, 1.2);
    this.anthrophony?.gain.setTargetAtTime(mix.anthrophony, now, 1.2);
    this.updateListener(frame.camera);

    if (!frame.paused) {
      const event = this.scheduler.next(frame.elapsed, frame.settings);
      if (event) this.playAmbientEvent(event, frame.camera.position);
    }
  }

  playNaturalFeedback(cue: NaturalAudioCue): void {
    if (!this.context || !this.geophony) return;
    const now = this.context.currentTime;
    if (cue.kind === "wade") {
      this.playNoiseBurst(this.geophony, now, 0.18, 520, 0.075 * cue.intensity, cue.position);
      this.playTone(this.geophony, now, 155, 88, 0.2, 0.035 * cue.intensity, cue.position, "sine");
      return;
    }
    if (cue.kind === "vegetation-brush") {
      this.playNoiseBurst(this.geophony, now, 0.12, 3200, 0.026 * cue.intensity, cue.position);
      return;
    }
    const cutoff = cue.kind === "stone-step" ? 1200 : 680;
    const level = cue.kind === "stone-step" ? 0.042 : 0.032;
    this.playNoiseBurst(this.geophony, now, 0.09, cutoff, level * cue.intensity, cue.position);
  }

  dispose(): void {
    for (const voice of [this.wind, this.water, this.rain, this.insects]) {
      for (const source of voice?.sources ?? []) {
        try { source.stop(); } catch { /* The source may already have ended. */ }
      }
    }
    for (const source of this.transientSources) {
      try { source.stop(); } catch { /* The source may already have ended. */ }
    }
    this.transientSources.clear();
    void this.context?.close();
    this.context = null;
    this.master = null;
    this.geophony = null;
    this.biophony = null;
    this.anthrophony = null;
  }

  private initialize(): void {
    this.context = new AudioContext();
    this.master = this.context.createGain();
    this.master.gain.value = 0;
    this.master.connect(this.context.destination);
    this.geophony = this.createBus();
    this.biophony = this.createBus();
    this.anthrophony = this.createBus();
    this.wind = this.createNoiseVoice("brown", 720, this.geophony);
    this.water = this.createNoiseVoice("pink", 1450, this.geophony);
    this.rain = this.createNoiseVoice("white", 5200, this.geophony);
    this.insects = this.createInsectBed(this.biophony);
  }

  private createBus(): GainNode {
    const gain = this.context!.createGain();
    gain.gain.value = 1;
    gain.connect(this.master!);
    return gain;
  }

  private createNoiseVoice(kind: "white" | "pink" | "brown", cutoff: number, output: AudioNode): Voice {
    const context = this.context!;
    const length = context.sampleRate * 4;
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    let brown = 0;
    let pinkA = 0;
    for (let index = 0; index < length; index += 1) {
      const white = this.random() * 2 - 1;
      brown = (brown + 0.02 * white) / 1.02;
      pinkA = 0.985 * pinkA + white * 0.06;
      data[index] = kind === "white" ? white * 0.34 : kind === "pink" ? pinkA * 0.7 : brown * 2.2;
    }
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const filter = context.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = cutoff;
    const gain = context.createGain();
    gain.gain.value = 0;
    source.connect(filter).connect(gain).connect(output);
    source.start();
    return { sources: [source], gain };
  }

  private createInsectBed(output: AudioNode): Voice {
    const context = this.context!;
    const gain = context.createGain();
    gain.gain.value = 0;
    gain.connect(output);
    const sources: AudioScheduledSourceNode[] = [];
    for (const frequency of [3270, 4160]) {
      const oscillator = context.createOscillator();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      const tremolo = context.createOscillator();
      tremolo.frequency.value = 7.5 + this.random() * 3;
      const tremoloGain = context.createGain();
      tremoloGain.gain.value = 0.45;
      const voiceGain = context.createGain();
      voiceGain.gain.value = 0.5;
      tremolo.connect(tremoloGain).connect(voiceGain.gain);
      oscillator.connect(voiceGain).connect(gain);
      oscillator.start();
      tremolo.start();
      sources.push(oscillator, tremolo);
    }
    return { sources, gain };
  }

  private playAmbientEvent(kind: AmbientEventKind, listener: THREE.Vector3): void {
    const context = this.context!;
    const now = context.currentTime;
    const position = this.randomAnchor(listener, kind === "boat" ? 12 : 7);
    if (kind === "bird") {
      const base = 1450 + this.random() * 750;
      this.playTone(this.biophony!, now, base, base * 1.32, 0.11, 0.027, position, "sine");
      this.playTone(this.biophony!, now + 0.16, base * 1.08, base * 0.82, 0.13, 0.022, position, "sine");
    } else if (kind === "insect") {
      this.playTone(this.biophony!, now, 3600 + this.random() * 900, 3300, 0.22, 0.014, position, "sine");
    } else if (kind === "frog") {
      this.playTone(this.biophony!, now, 135, 96, 0.28, 0.038, position, "sine");
    } else if (kind === "drip") {
      this.playTone(this.geophony!, now, 920 + this.random() * 540, 410, 0.09, 0.035, position, "sine");
    } else {
      this.playTone(this.anthrophony!, now, 178, 122, 0.8, 0.024, position, "triangle");
      this.playNoiseBurst(this.anthrophony!, now, 0.65, 480, 0.018, position);
    }
  }

  private playTone(
    output: AudioNode,
    start: number,
    fromFrequency: number,
    toFrequency: number,
    duration: number,
    level: number,
    position: THREE.Vector3,
    type: OscillatorType,
  ): void {
    const context = this.context!;
    const oscillator = context.createOscillator();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(fromFrequency, start);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, toFrequency), start + duration);
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(level, start + Math.min(0.025, duration * 0.25));
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain).connect(this.createSpatialOutput(output, position));
    this.trackTransient(oscillator);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  }

  private playNoiseBurst(
    output: AudioNode,
    start: number,
    duration: number,
    cutoff: number,
    level: number,
    position: THREE.Vector3,
  ): void {
    const context = this.context!;
    const buffer = context.createBuffer(1, Math.ceil(context.sampleRate * duration), context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < data.length; index += 1) data[index] = this.random() * 2 - 1;
    const source = context.createBufferSource();
    source.buffer = buffer;
    const filter = context.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = cutoff;
    const gain = context.createGain();
    gain.gain.setValueAtTime(level, start);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    source.connect(filter).connect(gain).connect(this.createSpatialOutput(output, position));
    this.trackTransient(source);
    source.start(start);
  }

  private createSpatialOutput(output: AudioNode, position: THREE.Vector3): AudioNode {
    const context = this.context!;
    if (typeof context.createPanner !== "function") return output;
    const panner = context.createPanner();
    panner.panningModel = "HRTF";
    panner.distanceModel = "inverse";
    panner.refDistance = 2.2;
    panner.maxDistance = 24;
    panner.rolloffFactor = 0.8;
    panner.positionX.value = position.x;
    panner.positionY.value = position.y;
    panner.positionZ.value = position.z;
    panner.connect(output);
    return panner;
  }

  private trackTransient(source: AudioScheduledSourceNode): void {
    this.transientSources.add(source);
    source.addEventListener("ended", () => this.transientSources.delete(source), { once: true });
  }

  private updateListener(camera: THREE.Camera): void {
    const listener = this.context!.listener;
    const direction = camera.getWorldDirection(new THREE.Vector3());
    if (listener.positionX) {
      listener.positionX.value = camera.position.x;
      listener.positionY.value = camera.position.y;
      listener.positionZ.value = camera.position.z;
      listener.forwardX.value = direction.x;
      listener.forwardY.value = direction.y;
      listener.forwardZ.value = direction.z;
      listener.upX.value = camera.up.x;
      listener.upY.value = camera.up.y;
      listener.upZ.value = camera.up.z;
    }
  }

  private randomAnchor(origin: THREE.Vector3, radius: number): THREE.Vector3 {
    const angle = this.random() * Math.PI * 2;
    const distance = radius * (0.55 + this.random() * 0.45);
    return origin.clone().add(new THREE.Vector3(
      Math.cos(angle) * distance,
      0.2 + this.random() * 2.6,
      Math.sin(angle) * distance,
    ));
  }

  private sampleNearbyWater(x: number, z: number): number {
    const radius = 7;
    let samples = 0;
    for (const [dx, dz] of [[radius, 0], [-radius, 0], [0, radius], [0, -radius]]) {
      if (this.world.getWaterDepth(x + dx!, z + dz!) > 0) samples += 1;
    }
    return samples / 4;
  }
}
